import { useEffect, useMemo, useState } from "react";

import { API_BASE } from "@/config/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AssetSearch } from "@/components/ui/asset-search";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getAllowedTransactionTypes, normalizeBookType } from "@/lib/book-types";
import { CalendarIcon, Loader2 } from "lucide-react";
import { fetchWithAuth } from "@/lib/api";

import type { Record, Asset, RecordWithAssets, Fee, BookType } from "@/schemas";

type TransactionType =
  | "buy"
  | "sell"
  | "exchange"
  | "currency_conversion"
  | "deposit"
  | "withdrawal"
  | "dividend";

interface EditRecordModalProps {
  open: boolean;
  onClose: () => void;
  record: RecordWithAssets;
  bookType?: BookType;
  onSubmit: (recordId: string, data: Record) => Promise<void>;
}

const limitDecimals = (value: string): string => {
  if (value === "" || value === ".") return value;
  const parts = value.split(".");
  if (parts.length === 2 && parts[1].length > 8) {
    return `${parts[0]}.${parts[1].slice(0, 8)}`;
  }
  return value;
};

const parsePositive = (value: string): number | null => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed) || parsed <= 0) return null;
  return parsed;
};

export function EditRecordModal({
  open,
  onClose,
  record,
  bookType,
  onSubmit,
}: EditRecordModalProps) {
  const normalizedBookType = normalizeBookType(bookType);
  const allowedTransactionTypes = useMemo(
    () => getAllowedTransactionTypes(normalizedBookType) as TransactionType[],
    [normalizedBookType],
  );
  const [transactionType, setTransactionType] = useState<TransactionType>("buy");
  const [date, setDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(false);

  const [selectedBuyAsset, setSelectedBuyAsset] = useState<Asset | null>(null);
  const [selectedSellAsset, setSelectedSellAsset] = useState<Asset | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const [fiatOptions, setFiatOptions] = useState<Asset[]>([]);
  const [selectedFiat, setSelectedFiat] = useState("");

  const [qtyBuyString, setQtyBuyString] = useState("");
  const [qtySellString, setQtySellString] = useState("");
  const [amountString, setAmountString] = useState("");

  const [feeAmountString, setFeeAmountString] = useState("");
  const [feeAssetId, setFeeAssetId] = useState("");

  const isTradeType =
    transactionType === "buy" ||
    transactionType === "sell" ||
    transactionType === "exchange" ||
    transactionType === "currency_conversion";

  const isCashBook = normalizedBookType === "cash";
  const tradeAssetType =
    isCashBook && transactionType === "currency_conversion"
      ? "fiat"
      : "stock,etf,fund,crypto,fiat";

  useEffect(() => {
    if (!open) return;

    const fetchFiatAssets = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/assets?type=fiat`, { method: "GET" });
        if (!response.ok) return;
        const data = await response.json();
        setFiatOptions(data.results || []);
      } catch (error) {
        console.error("Error fetching fiat assets:", error);
        setFiatOptions([]);
      }
    };

    void fetchFiatAssets();
  }, [open]);

  useEffect(() => {
    if (!open || !record) return;

    const type = (record.type as TransactionType) || "buy";
    setTransactionType(type);
    setDate(new Date(record.record_date));

    setSelectedBuyAsset(record.buyAsset || null);
    setSelectedSellAsset(record.sellAsset || null);
    setSelectedAsset(record.asset || null);

    setSelectedFiat(record.currency_asset_id || record.asset_id_buy || "");

    setQtyBuyString(record.qty_buy?.toString() || "");
    setQtySellString(record.qty_sell?.toString() || "");
    setAmountString(record.amount?.toString() || "");

    const firstFee = record.fees?.[0];
    setFeeAmountString(firstFee?.amount?.toString() || "");
    setFeeAssetId(firstFee?.asset_id || "");
  }, [open, record]);

  useEffect(() => {
    if (!open || allowedTransactionTypes.includes(transactionType)) return;

    const fallbackType = allowedTransactionTypes[0];
    if (fallbackType) {
      setTransactionType(fallbackType);
    }
  }, [allowedTransactionTypes, open, transactionType]);

  useEffect(() => {
    if (!open || fiatOptions.length === 0) return;
    if (selectedFiat) return;

    const firstFiat = fiatOptions[0]?.asset_id;
    if (firstFiat) setSelectedFiat(firstFiat);
  }, [open, fiatOptions, selectedFiat]);

  const validateForm = () => {
    if (!date) return false;

    if (isTradeType) {
      const buyQty = parsePositive(qtyBuyString);
      const sellQty = parsePositive(qtySellString);
      return !!selectedBuyAsset && !!selectedSellAsset && !!buyQty && !!sellQty;
    }

    if (transactionType === "deposit" || transactionType === "withdrawal") {
      const amount = parsePositive(amountString);
      return !!selectedFiat && !!amount;
    }

    if (transactionType === "dividend") {
      const amount = parsePositive(amountString);
      return !!selectedAsset && !!selectedFiat && !!amount;
    }

    return false;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const feeAmount = parsePositive(feeAmountString);
    const fee: Fee | undefined =
      feeAmount && feeAssetId ? { asset_id: feeAssetId, amount: feeAmount } : undefined;

    const baseData: Record = {
      record_id: record.record_id,
      book_id: record.book_id,
      type: transactionType,
      record_date: date.toISOString(),
      description: record.description,
      fees: fee ? [fee] : undefined,
    };

    let payload: Record = {
      ...baseData,
      asset_id_buy: undefined,
      asset_id_sell: undefined,
      qty_buy: undefined,
      qty_sell: undefined,
      asset_id: undefined,
      amount: undefined,
      currency_asset_id: undefined,
    };

    if (isTradeType) {
      payload = {
        ...baseData,
        asset_id_buy: selectedBuyAsset?.asset_id,
        asset_id_sell: selectedSellAsset?.asset_id,
        qty_buy: parsePositive(qtyBuyString) || undefined,
        qty_sell: parsePositive(qtySellString) || undefined,
      };
    } else if (transactionType === "deposit" || transactionType === "withdrawal") {
      payload = {
        ...baseData,
        asset_id: selectedFiat,
        amount: parsePositive(amountString) || undefined,
      };
    } else if (transactionType === "dividend") {
      payload = {
        ...baseData,
        asset_id: selectedAsset?.asset_id,
        currency_asset_id: selectedFiat,
        amount: parsePositive(amountString) || undefined,
      };
    }

    try {
      setIsLoading(true);
      await onSubmit(record.record_id, payload);
      onClose();
    } catch (error) {
      console.error("Error updating record:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Transaction</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <div className="grid gap-2">
            <Label>Transaction Type</Label>
            <Select
              value={transactionType}
              onValueChange={(value) => setTransactionType(value as TransactionType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedTransactionTypes.includes("buy") && (
                  <SelectItem value="buy">Buy</SelectItem>
                )}
                {allowedTransactionTypes.includes("sell") && (
                  <SelectItem value="sell">Sell</SelectItem>
                )}
                {allowedTransactionTypes.includes("exchange") && (
                  <SelectItem value="exchange">Exchange</SelectItem>
                )}
                {allowedTransactionTypes.includes("currency_conversion") && (
                  <SelectItem value="currency_conversion">FX Conversion</SelectItem>
                )}
                {allowedTransactionTypes.includes("deposit") && (
                  <SelectItem value="deposit">Deposit</SelectItem>
                )}
                {allowedTransactionTypes.includes("withdrawal") && (
                  <SelectItem value="withdrawal">Withdrawal</SelectItem>
                )}
                {allowedTransactionTypes.includes("dividend") && (
                  <SelectItem value="dividend">Dividend</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {isTradeType && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label>
                  {transactionType === "currency_conversion"
                    ? "Buy Currency"
                    : "Buy Asset"}
                </Label>
                <AssetSearch
                  value={selectedBuyAsset?.asset_id}
                  onSelect={setSelectedBuyAsset}
                  placeholder={
                    transactionType === "currency_conversion"
                      ? "Search buy currency..."
                      : "Search buy asset..."
                  }
                  assetType={tradeAssetType}
                />
              </div>
              <div className="grid gap-2">
                <Label>
                  {transactionType === "currency_conversion"
                    ? "Sell Currency"
                    : "Sell Asset"}
                </Label>
                <AssetSearch
                  value={selectedSellAsset?.asset_id}
                  onSelect={setSelectedSellAsset}
                  placeholder={
                    transactionType === "currency_conversion"
                      ? "Search sell currency..."
                      : "Search sell asset..."
                  }
                  assetType={tradeAssetType}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label>Buy Quantity</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={qtyBuyString}
                    placeholder="0"
                    onChange={(event) => {
                      const value = limitDecimals(event.target.value);
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setQtyBuyString(value);
                      }
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Sell Quantity</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={qtySellString}
                    placeholder="0"
                    onChange={(event) => {
                      const value = limitDecimals(event.target.value);
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setQtySellString(value);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {(transactionType === "deposit" || transactionType === "withdrawal") && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label>Currency</Label>
                <Select value={selectedFiat} onValueChange={setSelectedFiat}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {fiatOptions.map((fiat) => (
                      <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                        {fiat.symbol} - {fiat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Amount</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={amountString}
                  placeholder="0"
                  onChange={(event) => {
                    const value = limitDecimals(event.target.value);
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      setAmountString(value);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {transactionType === "dividend" && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label>Asset</Label>
                <AssetSearch
                  value={selectedAsset?.asset_id}
                  onSelect={setSelectedAsset}
                  placeholder="Search dividend asset..."
                  assetType="stock,etf,fund,crypto"
                  enableCompanyFlow={true}
                />
              </div>
              <div className="grid gap-2">
                <Label>Currency</Label>
                <Select value={selectedFiat} onValueChange={setSelectedFiat}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {fiatOptions.map((fiat) => (
                      <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                        {fiat.symbol} - {fiat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Amount</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={amountString}
                  placeholder="0"
                  onChange={(event) => {
                    const value = limitDecimals(event.target.value);
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      setAmountString(value);
                    }
                  }}
                />
              </div>
            </div>
          )}

          <div className="grid gap-2">
            <Label>Fee (Optional)</Label>
            <div className="flex gap-2">
              <Input
                type="text"
                inputMode="decimal"
                value={feeAmountString}
                onChange={(event) => {
                  const value = limitDecimals(event.target.value);
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setFeeAmountString(value);
                  }
                }}
                placeholder="0.00"
                className="flex-1"
              />
              <Select value={feeAssetId} onValueChange={setFeeAssetId}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent>
                  {fiatOptions.map((fiat) => (
                    <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                      {fiat.symbol} - {fiat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Transaction Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(newDate) => {
                    if (newDate) setDate(newDate);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!validateForm() || isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Transaction
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
